import { forwardRef } from "react";
import { Worksheet } from "@/types/worksheet";

interface PrintableWorksheetProps {
  worksheet: Worksheet;
}

const PrintableWorksheet = forwardRef<HTMLDivElement, PrintableWorksheetProps>(
  ({ worksheet }, ref) => {
    const renderPrintableContent = () => {
      switch (worksheet.type.category) {
        case 'puzzle':
          return renderPrintablePuzzle();
        case 'matching':
          return renderPrintableMatching();
        case 'sorting':
          return renderPrintableSorting();
        default:
          return <div>Worksheet type not supported for printing.</div>;
      }
    };

    const renderPrintablePuzzle = () => {
      const gridSize = 12;
      const grid = generatePrintGrid();
      const actualWords = generateActualWordsInGrid();
      
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="grid grid-cols-12 gap-1 inline-block border-2 border-gray-800 p-2">
              {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className="w-6 h-6 border border-gray-400 flex items-center justify-center text-xs font-bold"
                  >
                    {cell}
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-bold mb-3">Find these words:</h3>
            <div className="grid grid-cols-3 gap-4">
              {actualWords.map((word, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-4 h-4 border border-gray-400"></div>
                  <span className="text-sm">{word}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const renderPrintableMatching = () => {
      const leftItems = worksheet.content.items.map((item: any) => item.left);
      const rightItems = [...worksheet.content.items.map((item: any) => item.right)].sort(() => Math.random() - 0.5);
      
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4 text-center">Match these...</h3>
              <div className="space-y-3">
                {leftItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center text-sm font-bold">
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div className="border border-gray-300 p-2 flex-1 rounded">
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-4 text-center">...with these!</h3>
              <div className="space-y-3">
                {rightItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div className="border border-gray-300 p-2 flex-1 rounded">
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h3 className="text-lg font-bold mb-3">Write your answers:</h3>
            <div className="grid grid-cols-2 gap-4">
              {leftItems.map((_, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="font-bold">{String.fromCharCode(65 + index)}</span>
                  <span>=</span>
                  <div className="border-b border-gray-400 w-12 h-6"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const renderPrintableSorting = () => {
      const allItems = worksheet.content.items.flatMap((category: any) => 
        category.items.map((item: any) => item.name)
      ).sort(() => Math.random() - 0.5);
      
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold mb-3">Items to Sort:</h3>
            <div className="border-2 border-dashed border-gray-400 p-3 rounded">
              <div className="grid grid-cols-3 gap-2">
                {allItems.map((item, index) => (
                  <div key={index} className="border border-gray-300 p-2 text-center text-sm rounded">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {worksheet.content.items.map((category: any, index: number) => (
              <div key={index} className="border-2 border-gray-400 rounded p-4">
                <h4 className="text-lg font-bold text-center mb-4 bg-gray-100 p-2 rounded">
                  {category.name}
                </h4>
                <div className="space-y-2">
                  {[...Array(Math.max(3, category.items.length))].map((_, itemIndex) => (
                    <div key={itemIndex} className="border-b border-gray-300 h-8"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const generateActualWordsInGrid = (): string[] => {
      const words = worksheet.content.items.map((item: any) => item.word?.toUpperCase() || '').filter(Boolean);
      const gridSize = 12;
      const maxWords = 9;
      
      // Filter words that can actually fit in the grid
      const validWords = words.filter(word => word.length >= 3 && word.length <= gridSize - 2);
      
      // Return up to maxWords that can realistically be placed
      return validWords.slice(0, maxWords);
    };

    const generatePrintGrid = () => {
      const size = 12;
      const grid: string[][] = Array(size).fill(null).map(() => Array(size).fill(''));
      const wordsToPlace = generateActualWordsInGrid();
      const placedWords: string[] = [];
      
      // Initialize with random letters first
      for (let i = 0; i < size; i++) {
        for (let j = 0; j < size; j++) {
          grid[i][j] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        }
      }

      // Place words in the grid using improved algorithm
      wordsToPlace.forEach((word) => {
        let placed = false;
        let attempts = 0;
        
        while (!placed && attempts < 100) {
          const direction = Math.floor(Math.random() * 3); // 0: horizontal, 1: vertical, 2: diagonal
          let maxStartRow, maxStartCol;
          
          switch (direction) {
            case 0: // horizontal
              maxStartRow = size - 1;
              maxStartCol = size - word.length;
              break;
            case 1: // vertical
              maxStartRow = size - word.length;
              maxStartCol = size - 1;
              break;
            case 2: // diagonal
              maxStartRow = size - word.length;
              maxStartCol = size - word.length;
              break;
            default:
              maxStartRow = maxStartCol = 0;
          }
          
          if (maxStartRow >= 0 && maxStartCol >= 0) {
            const startRow = Math.floor(Math.random() * (maxStartRow + 1));
            const startCol = Math.floor(Math.random() * (maxStartCol + 1));
            
            // Place the word
            for (let i = 0; i < word.length; i++) {
              let row = startRow;
              let col = startCol;
              
              switch (direction) {
                case 0: col += i; break; // horizontal
                case 1: row += i; break; // vertical
                case 2: row += i; col += i; break; // diagonal
              }
              
              grid[row][col] = word[i];
            }
            
            placedWords.push(word);
            placed = true;
          }
          attempts++;
        }
      });

      return grid;
    };

    return (
      <div ref={ref} className="print-worksheet bg-white p-8 max-w-4xl mx-auto">
        <style>
          {`
            @media print {
              .print-worksheet {
                font-size: 12px;
                color: black !important;
              }
              * {
                color: black !important;
                background: white !important;
              }
            }
          `}
        </style>
        
        <div className="text-center mb-8 border-b-2 border-gray-800 pb-4">
          <h1 className="text-2xl font-bold text-gray-800">{worksheet.title}</h1>
          <div className="flex justify-between items-center mt-2">
            <span className="text-sm">Grade: {worksheet.grade}</span>
            <span className="text-sm">Subject: {worksheet.subject}</span>
            <span className="text-sm">Difficulty: {worksheet.difficulty}</span>
          </div>
        </div>

        <div className="mb-6 p-4 border border-gray-400 rounded">
          <h2 className="text-lg font-bold mb-2">Instructions:</h2>
          <p className="text-gray-700">{worksheet.content.instructions}</p>
        </div>

        {renderPrintableContent()}

        <div className="mt-8 pt-4 border-t border-gray-400 text-center text-sm text-gray-600">
          <p>Student Name: ________________________  Date: ___________</p>
        </div>
      </div>
    );
  }
);

PrintableWorksheet.displayName = "PrintableWorksheet";

export default PrintableWorksheet;
